import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'wouter';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from './dialog';
import { Input } from './input';
import {
  Activity,
  Command,
  FileCheck2,
  LayoutDashboard,
  Scale,
  Search,
  Target,
  ArrowRight,
  ChartNoAxesCombined,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface CommandItem {
  id: string;
  label: string;
  description?: string;
  icon: React.ReactNode;
  action: () => void;
  keywords?: string[];
  category: 'navigation' | 'action' | 'search';
}

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [, setLocation] = useLocation();

  const navigationItems: CommandItem[] = useMemo(
    () => [
      {
        id: 'decision-room',
        label: 'Decision',
        description: 'Verdict and blockers',
        icon: <LayoutDashboard className="h-4 w-4" />,
        action: () => setLocation('/'),
        keywords: ['home', 'readiness', 'blockers', 'promotion'],
        category: 'navigation',
      },
      {
        id: 'strategy',
        label: 'Strategy',
        description: 'Mandate and limits',
        icon: <Target className="h-4 w-4" />,
        action: () => setLocation('/strategy'),
        keywords: ['candidate', 'mandate', 'construction', 'regime'],
        category: 'navigation',
      },
      {
        id: 'performance-risk',
        label: 'Performance',
        description: 'Returns and robustness',
        icon: <ChartNoAxesCombined className="h-4 w-4" />,
        action: () => setLocation('/performance'),
        keywords: ['returns', 'risk', 'sharpe', 'drawdown', 'robustness'],
        category: 'navigation',
      },
      {
        id: 'operations',
        label: 'Operations',
        description: 'Runtime and venues',
        icon: <Activity className="h-4 w-4" />,
        action: () => setLocation('/operations'),
        keywords: ['runtime', 'testnet', 'release', 'readiness'],
        category: 'navigation',
      },
      {
        id: 'evidence',
        label: 'Evidence',
        description: 'Artifacts and hashes',
        icon: <FileCheck2 className="h-4 w-4" />,
        action: () => setLocation('/evidence'),
        keywords: ['artifacts', 'hashes', 'audit', 'provenance'],
        category: 'navigation',
      },
      {
        id: 'governance',
        label: 'Governance',
        description: 'Promotion gates',
        icon: <Scale className="h-4 w-4" />,
        action: () => setLocation('/governance'),
        keywords: ['gates', 'owners', 'approval', 'promotion'],
        category: 'navigation',
      },
    ],
    [setLocation],
  );

  const filteredItems = useMemo(() => {
    if (!query) return navigationItems;

    const lowerQuery = query.toLowerCase();
    return navigationItems.filter((item) => {
      const matchLabel = item.label.toLowerCase().includes(lowerQuery);
      const matchDescription = item.description?.toLowerCase().includes(lowerQuery);
      const matchKeywords = item.keywords?.some((k) => k.includes(lowerQuery));
      return matchLabel || matchDescription || matchKeywords;
    });
  }, [query, navigationItems]);

  // Reset selection when query changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  // Reset query when dialog closes
  useEffect(() => {
    if (!open) {
      setQuery('');
      setSelectedIndex(0);
    }
  }, [open]);

  const executeSelected = useCallback(() => {
    const item = filteredItems[selectedIndex];
    if (item) {
      item.action();
      onOpenChange(false);
    }
  }, [filteredItems, selectedIndex, onOpenChange]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((i) => Math.min(i + 1, filteredItems.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((i) => Math.max(i - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          executeSelected();
          break;
        case 'Escape':
          e.preventDefault();
          onOpenChange(false);
          break;
      }
    },
    [filteredItems.length, executeSelected, onOpenChange],
  );

  // Group items by category
  const groupedItems = useMemo(() => {
    const groups: Record<string, CommandItem[]> = {};
    filteredItems.forEach((item) => {
      if (!groups[item.category]) {
        groups[item.category] = [];
      }
      groups[item.category].push(item);
    });
    return groups;
  }, [filteredItems]);

  const categoryLabels: Record<string, string> = {
    navigation: 'Navigation',
    action: 'Actions',
    search: 'Search',
  };

  let itemIndex = -1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg p-0 gap-0 bg-navy-800 border-slate-600/30 overflow-hidden"
        onKeyDown={handleKeyDown}
      >
        <DialogTitle className="sr-only">Allocator navigation</DialogTitle>
        <DialogDescription className="sr-only">
          Navigate between the six allocator evidence views.
        </DialogDescription>
        <div className="flex items-center border-b border-slate-600/30 px-4">
          <Search className="h-4 w-4 text-slate-500 mr-3" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search allocator views..."
            className="border-0 bg-transparent focus-visible:ring-0 px-0 py-4 text-slate-100 placeholder:text-slate-500"
            autoFocus
          />
          <div className="flex items-center gap-1 text-xs text-slate-500">
            <kbd className="px-1.5 py-0.5 rounded bg-slate-700/50 border border-slate-600/30">
              <Command className="h-3 w-3 inline" />
            </kbd>
            <span>K</span>
          </div>
        </div>

        <div className="max-h-80 overflow-y-auto custom-scrollbar py-2">
          {filteredItems.length === 0 ? (
            <div className="py-8 text-center text-slate-500">
              <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No results found</p>
            </div>
          ) : (
            Object.entries(groupedItems).map(([category, items]) => (
              <div key={category}>
                <div className="px-4 py-1.5 text-xs font-medium text-slate-500 uppercase tracking-wider">
                  {categoryLabels[category]}
                </div>
                {items.map((item) => {
                  itemIndex++;
                  const isSelected = itemIndex === selectedIndex;
                  const currentIndex = itemIndex;

                  return (
                    <button
                      key={item.id}
                      onClick={() => {
                        item.action();
                        onOpenChange(false);
                      }}
                      onMouseEnter={() => setSelectedIndex(currentIndex)}
                      className={cn(
                        'w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors',
                        isSelected
                          ? 'bg-teal-500/10 text-teal-400'
                          : 'text-slate-300 hover:bg-slate-700/30',
                      )}
                    >
                      <div
                        className={cn(
                          'flex-shrink-0 p-2 rounded-lg',
                          isSelected ? 'bg-teal-500/20' : 'bg-slate-700/50',
                        )}
                      >
                        {item.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium">{item.label}</div>
                        {item.description && (
                          <div className="text-xs text-slate-500 truncate">{item.description}</div>
                        )}
                      </div>
                      {isSelected && <ArrowRight className="h-4 w-4 flex-shrink-0 text-teal-400" />}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        <div className="border-t border-slate-600/30 px-4 py-2 flex items-center justify-between text-xs text-slate-500">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 rounded bg-slate-700/50 border border-slate-600/30">
                ↑
              </kbd>
              <kbd className="px-1.5 py-0.5 rounded bg-slate-700/50 border border-slate-600/30">
                ↓
              </kbd>
              <span className="ml-1">Navigate</span>
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 rounded bg-slate-700/50 border border-slate-600/30">
                ↵
              </kbd>
              <span className="ml-1">Select</span>
            </span>
          </div>
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 rounded bg-slate-700/50 border border-slate-600/30">
              Esc
            </kbd>
            <span className="ml-1">Close</span>
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
